/**
 * Email Template API Client
 * ADR-090: Email Template System
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface VariableDefinition {
  key: string;
  label: string;
  required: boolean;
  example: string;
}

export interface TemplateBlock {
  content: string;
  editable: boolean;
}

export interface CtaBlock extends TemplateBlock {
  url: string;
}

export interface TemplateBlocks {
  greeting: TemplateBlock;
  body: TemplateBlock;
  cta?: CtaBlock | undefined;
}

export interface EmailTemplateListItem {
  slug: string;
  language: string;
  name: string;
  subject: string;
  isCustomized: boolean;
  isActive: boolean;
  version: number;
  updatedAt?: string | undefined;
}

export interface ResolvedTemplate {
  slug: string;
  name: string;
  subject: string;
  blocks: TemplateBlocks;
  variables: VariableDefinition[];
  source: 'db' | 'code';
}

export interface PreviewResult {
  subject: string;
  html: string;
  sampleData: Record<string, string>;
}

export interface EmailLogItem {
  id: string;
  templateSlug: string;
  recipientEmail: string;
  subject: string;
  status: string;
  errorMessage?: string | undefined;
  jobId?: string | undefined;
  retryCount: number;
  sentAt?: string | undefined;
  createdAt: string;
}

export interface EmailLogStats {
  sent: number;
  failed: number;
  rejected: number;
  queued: number;
}

export interface EmailLogQueryResult {
  items: EmailLogItem[];
  total: number;
  page: number;
  limit: number;
  stats: EmailLogStats;
}

export interface TemplateVersion {
  id: string;
  version: number;
  subject: string;
  blocks: TemplateBlocks;
  changedBy: string;
  changeNote?: string | undefined;
  createdAt: string;
}

// ============================================
// TEMPLATE API
// ============================================

export async function listEmailTemplates(language?: string) {
  const qs = language ? `?language=${language}` : '';
  return api.get<{ data: EmailTemplateListItem[] }>(`/settings/email-templates${qs}`);
}

export async function getEmailTemplate(slug: string, language = 'hu') {
  return api.get<{ data: ResolvedTemplate }>(
    `/settings/email-templates/${slug}?language=${language}`
  );
}

export async function updateEmailTemplate(
  slug: string,
  language: string,
  body: {
    name?: string | undefined;
    subject?: string | undefined;
    blocks?: Partial<TemplateBlocks> | undefined;
    isActive?: boolean | undefined;
  }
) {
  return api.patch<{ data: Record<string, unknown> }>(
    `/settings/email-templates/${slug}?language=${language}`,
    body
  );
}

export async function resetEmailTemplate(slug: string, language: string) {
  return api.delete(`/settings/email-templates/${slug}?language=${language}`);
}

export async function previewEmailTemplate(
  slug: string,
  language: string,
  variables?: Record<string, string>
) {
  return api.post<{ data: PreviewResult }>(
    `/settings/email-templates/${slug}/preview?language=${language}`,
    { variables }
  );
}

export async function sendTestEmail(slug: string, email?: string) {
  return api.post<{ data: { message: string } }>(`/settings/email-templates/${slug}/test-email`, {
    email,
  });
}

// ============================================
// VERSION API (Story 3.3)
// ============================================

export async function listTemplateVersions(slug: string, language = 'hu') {
  return api.get<{ data: TemplateVersion[] }>(
    `/settings/email-templates/${slug}/versions?language=${language}`
  );
}

export async function rollbackTemplate(slug: string, language: string, versionId: string) {
  return api.post<{ data: { message: string } }>(
    `/settings/email-templates/${slug}/rollback?language=${language}`,
    { versionId }
  );
}

// ============================================
// EMAIL LOG API
// ============================================

export async function listEmailLogs(params?: {
  status?: string | undefined;
  templateSlug?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.templateSlug) query.set('templateSlug', params.templateSlug);
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return api.get<{ data: EmailLogQueryResult }>(`/settings/email-logs${qs ? `?${qs}` : ''}`);
}

export async function resendEmail(logId: string) {
  return api.post<{ data: { success: boolean; message: string } }>(
    `/settings/email-logs/${logId}/resend`,
    {}
  );
}
